// src/pages/BrowseAssets.js
import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import { useNavigate } from "react-router-dom";

import { Search, Filter } from "lucide-react";

export default function BrowseAssets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // ✅ UPDATED: Categories to match your Firestore data
  const categories = [
    { value: "all", label: "All Categories" },
    { value: "ict-equipment", label: "ICT Equipment" },
    { value: "devices", label: "Devices" },
    { value: "lab equipment", label: "Lab Equipment" }
  ];

  const statuses = [
    { value: "all", label: "All Status" },
    { value: "available", label: "Available" },
    { value: "borrowed", label: "Borrowed" },
    { value: "missing", label: "Missing" },
    { value: "under maintenance", label: "Under Maintenance" }
  ];

  useEffect(() => {
    // Set user as student by default since this is the student browsing page
    setUser({ uid: "anonymous", role: "student" });
    setLoading(false);
  }, []);

  // Load assets
  useEffect(() => {
    loadAssets();
  }, []);

  const loadAssets = async () => {
    try {
      setLoading(true);
      console.log("Loading all assets...");

      const assetsRef = collection(db, "assets");
      const querySnapshot = await getDocs(assetsRef);
      
      const assetsData = [];
      querySnapshot.forEach((doc) => {
        if (doc.id && doc.exists()) {
          const assetData = doc.data();
          assetsData.push({
            id: doc.id,
            ...assetData
          });
        }
      });

      console.log("Loaded assets:", assetsData);
      
      // ✅ DEBUG: Log all unique categories found
      const uniqueCategories = [...new Set(assetsData.map(asset => asset.category))];
      console.log("Unique categories in database:", uniqueCategories);
      
      setAssets(assetsData);
    } catch (error) {
      console.error("Error loading assets:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter assets based on search, category, and status
  const filteredAssets = assets.filter(asset => {
    // Search filter
    const matchesSearch = searchQuery === "" || 
      asset.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.location?.toLowerCase().includes(searchQuery.toLowerCase());

    // ✅ FIXED: Category filter - exact match with your Firestore data
    const matchesCategory = selectedCategory === "all" || 
      asset.category === selectedCategory;

    // Status filter
    const matchesStatus = selectedStatus === "all" || 
      asset.status?.toLowerCase() === selectedStatus.toLowerCase();

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleAssetClick = (assetId) => {
    if (!assetId) {
      console.error("Cannot navigate: Invalid asset ID");
      return;
    }
    navigate(`/asset-detail/${assetId}`);
  };

  const handleRequestAsset = (assetId) => {
    if (!assetId) {
      console.error("Cannot request: Invalid asset ID");
      return;
    }
    navigate("/item-request", { state: { selectedAssetId: assetId } });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedStatus("all");
    setShowFilters(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 mt-2">Loading assets...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Search Bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search assets by name, description, serial number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Filter Toggle Button */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Filter className="w-4 h-4" />
          <span>Filters</span>
          {(selectedCategory !== "all" || selectedStatus !== "all") && (
            <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {(selectedCategory !== "all" ? 1 : 0) + (selectedStatus !== "all" ? 1 : 0)}
            </span>
          )}
        </button>

        {(selectedCategory !== "all" || selectedStatus !== "all" || searchQuery) && (
          <button
            onClick={clearFilters}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Filters Dropdown */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
          {/* Category Filter */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statuses.map((status) => (
                <button
                  key={status.value}
                  onClick={() => setSelectedStatus(status.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedStatus === status.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Showing {filteredAssets.length} of {assets.length} assets
          {(searchQuery || selectedCategory !== "all" || selectedStatus !== "all") && (
            <span className="text-blue-600">
              {" "}(filtered)
            </span>
          )}
        </p>
      </div>

      {/* Assets Grid */}
      <div className="grid grid-cols-1 gap-4">
        {filteredAssets.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-500 mb-2">No assets found</p>
            <p className="text-sm text-gray-400 mb-4">
              {searchQuery || selectedCategory !== "all" || selectedStatus !== "all" 
                ? "Try adjusting your filters or search terms"
                : "No assets available in the system"
              }
            </p>
            {(searchQuery || selectedCategory !== "all" || selectedStatus !== "all") && (
              <button 
                onClick={clearFilters}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          filteredAssets.map((asset) => (
            <AssetCard 
              key={asset.id} 
              asset={asset}
              onView={() => handleAssetClick(asset.id)}
              onRequest={() => handleRequestAsset(asset.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Asset Card Component
function AssetCard({ asset, onView, onRequest }) {
  if (!asset) {
    return (
      <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
        <p className="text-gray-500">Invalid asset data</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "bg-green-100 text-green-800";
      case "borrowed":
        return "bg-blue-100 text-blue-800";
      case "missing":
        return "bg-red-100 text-red-800";
      case "under maintenance":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
      case "ict-equipment":
        return "bg-purple-100 text-purple-800";
      case "devices":
        return "bg-indigo-100 text-indigo-800";
      case "lab equipment":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isAvailable = asset.status?.toLowerCase() === "available";

  return (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <h3 className="font-semibold text-gray-800 text-lg flex-1 pr-2">
          {asset.name || "Unnamed Asset"}
        </h3>
        <div className="flex flex-col gap-1 items-end">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
            {asset.status || "Unknown"}
          </span>
          {asset.category && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(asset.category)}`}>
              {asset.category === "ict-equipment" ? "ICT Equipment" : asset.category}
            </span>
          )}
        </div>
      </div>

      <p className="text-gray-600 text-sm mb-2 line-clamp-2">
        {asset.description || "No description available"}
      </p>

      <div className="text-xs text-gray-500 space-y-1 mb-3">
        {asset.serialNumber && (
          <p>Serial: {asset.serialNumber}</p>
        )}
        {asset.location && (
          <p>Location: {asset.location}</p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onView}
          className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
        >
          View Details
        </button>
        <button
          onClick={onRequest}
          disabled={!isAvailable}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition ${
            isAvailable
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isAvailable ? "Request" : "Unavailable"}
        </button>
      </div>
    </div>
  );
}
